import { useState } from 'react'
import type { JSX } from 'react'
import { NavLink } from 'react-router-dom'

import {
  TriangleAlertIcon,
  Badge,
  Button,
  Card,
  ChevronDownIcon,
  CopyIcon,
  DropdownMenu,
  Flex,
  Heading,
  KeyRoundIcon,
  Link,
  Notice,
  Skeleton,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import { useOrganizationSwitcher } from '~publish/components/MainSidebar/components/OrganizationSwitcher/useOrganizationSwitcher'
import { useAccount, useIsOwner } from '~publish/contexts/AccountContext'
import type { PersonalAccessToken } from '../../../DeveloperDashboard'

import { GenerateApiKeyCard } from '../../../DeveloperDashboard/components/GenerateApiKeyCard'
import { maskToken } from '../../../DeveloperDashboard/helpers/maskToken'
import { ApiKeySafetyNotice } from '../../../shared/components/ApiKeySafetyNotice'
import safetyNoticeStyles from '../../../shared/components/ApiKeySafetyNotice/ApiKeySafetyNotice.module.css'
import { CopyButton } from '../../../shared/components/CopyButton'
import {
  getDaysUntilExpiration,
  isTokenExpired,
  isTokenExpiringSoon,
} from '../../../shared/utils/tokenExpiration'

import styles from './ApiKeySection.module.css'

const SwitchOrgBanner = (): JSX.Element | null => {
  const { account } = useAccount()
  const { organizations, switchOrganization } = useOrganizationSwitcher()

  const ownedOrganization = organizations.find(
    (org) => org.ownerEmail === account?.email,
  )

  const handleSwitch = async (): Promise<void> => {
    if (ownedOrganization) {
      await switchOrganization(ownedOrganization.id)
    }
  }

  return (
    <Card className={styles.switchOrgBanner}>
      <Flex direction="column" gap="xs">
        <Flex justify="between" align="center" gap="md" fullWidth>
          <Heading as="h3" size="xsmall">
            For API Keys, switch to your organization
          </Heading>

          {ownedOrganization && (
            <Button
              variant="secondary"
              size="small"
              onClick={handleSwitch}
              className={styles.switchOrgButton}
            >
              Switch to Your Organization
            </Button>
          )}
        </Flex>

        <Text color="subtle">
          Only organization owners can manage API keys. These provide access to
          your data across all organizations you belong to.
        </Text>
      </Flex>
    </Card>
  )
}

interface ApiKeySectionProps {
  integrationName: string
  tokens: PersonalAccessToken[]
  isLoading?: boolean
  selectedToken: PersonalAccessToken | null
  onSelectToken: (token: PersonalAccessToken) => void
  onGenerateClick: () => void
}

export function ApiKeySection({
  integrationName,
  tokens,
  isLoading = false,
  selectedToken,
  onSelectToken,
  onGenerateClick,
}: ApiKeySectionProps): JSX.Element {
  const [showSafetyNotice, setShowSafetyNotice] = useState(false)
  const isOwner = useIsOwner()
  const hasMultipleKeys = tokens.length > 1
  const selectedTokenExpired = selectedToken
    ? isTokenExpired(selectedToken.expiresAt)
    : false

  if (!isOwner) {
    return <SwitchOrgBanner />
  }

  return (
    <div className={styles.apiKeySection}>
      <Flex justify="between" align="center">
        {hasMultipleKeys ? (
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <Button variant="tertiary" size="small">
                <Flex align="center" gap="xs">
                  {selectedToken?.name ?? 'API Key'}
                  {selectedTokenExpired && (
                    <Badge size="small" className={styles.expiredBadge}>
                      Expired
                    </Badge>
                  )}
                </Flex>
                <ChevronDownIcon />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="start">
              {tokens.map((token) => {
                const expired = isTokenExpired(token.expiresAt)
                const expiringSoon = isTokenExpiringSoon(token.expiresAt)
                return (
                  <DropdownMenu.CheckboxItem
                    key={token.accessToken}
                    checked={token.accessToken === selectedToken?.accessToken}
                    onSelect={() => {
                      setShowSafetyNotice(false)
                      onSelectToken(token)
                    }}
                  >
                    <Flex align="center" gap="xs">
                      {token.name ?? 'API Key'}
                      {expired && (
                        <Badge size="small" className={styles.expiredBadge}>
                          Expired
                        </Badge>
                      )}
                      {expiringSoon && (
                        <Badge variant="warning" size="small">
                          {getDaysUntilExpiration(token.expiresAt)}d left
                        </Badge>
                      )}
                    </Flex>
                  </DropdownMenu.CheckboxItem>
                )
              })}
            </DropdownMenu.Content>
          </DropdownMenu>
        ) : (
          <Text weight="medium">{selectedToken?.name ?? 'API Key'}</Text>
        )}
        <Button
          as={NavLink}
          to="/settings/api"
          variant="secondary"
          size="small"
        >
          Manage
        </Button>
      </Flex>

      {isLoading ? (
        <Skeleton className={styles.tokenSkeleton} />
      ) : selectedToken ? (
        <>
          <Card padding="none" className={styles.keyCard}>
            <div className={styles.keyRow}>
              <div className={styles.iconBadge}>
                <KeyRoundIcon />
              </div>
              {selectedTokenExpired ? (
                <Flex align="center" gap="xs" className={styles.tokenContent}>
                  <Text className={styles.maskedToken} color="subtle">
                    Key expired
                  </Text>
                  <Badge size="small" className={styles.expiredBadge}>
                    <TriangleAlertIcon /> Expired
                  </Badge>
                </Flex>
              ) : (
                <Text className={styles.maskedToken} color="subtle">
                  {maskToken(selectedToken.accessToken)}
                </Text>
              )}
              <Flex gap="xs" className={styles.actions}>
                {selectedTokenExpired ? (
                  <Tooltip content="This API key has expired. Regenerate it or create a new one.">
                    <span
                      tabIndex={0}
                      // The focusable proxy exposes the disabled copy action's
                      // explanation; nesting a semantic button would be invalid.
                      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
                      role="button"
                      aria-disabled="true"
                      aria-label="Copy token (expired)"
                    >
                      <Button
                        variant="tertiary"
                        disabled
                        aria-hidden="true"
                        tabIndex={-1}
                      >
                        <CopyIcon />
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <CopyButton
                    textToCopy={selectedToken.accessToken}
                    label="Copy token"
                    onCopy={() => setShowSafetyNotice(true)}
                  />
                )}
              </Flex>
            </div>
            <ApiKeySafetyNotice
              visible={showSafetyNotice}
              onDismiss={() => setShowSafetyNotice(false)}
              className={safetyNoticeStyles.noTopRadius}
            />
          </Card>
          {selectedTokenExpired && (
            <Notice variant="warning">
              <Notice.Text>
                This API key has expired and can no longer authenticate
                requests.{' '}
                <Link as={NavLink} to="/settings/api">
                  Manage your keys
                </Link>{' '}
                to regenerate it or create a new one.
              </Notice.Text>
            </Notice>
          )}
        </>
      ) : (
        <GenerateApiKeyCard
          description={`Generate an API key to integrate Buffer with ${integrationName}.`}
          onGenerate={onGenerateClick}
        />
      )}
    </div>
  )
}
